import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, AlertCircle, Shield, Eye, EyeOff } from 'lucide-react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    OAuthProvider,
    sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../lib/firebase';

export function Login() {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [view, setView] = useState<'auth' | 'forgot'>('auth');
    const navigate = useNavigate();

    const handleGoogleLogin = async () => {
        setLoadingProvider('google');
        setError(null);
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            navigate('/');
        } catch (err: any) {
            setError('Google sign-in failed. Please try again.');
        } finally {
            setLoadingProvider(null);
        }
    };

    const handleAppleLogin = async () => {
        setLoadingProvider('apple');
        setError(null);
        try {
            const provider = new OAuthProvider('apple.com');
            await signInWithPopup(auth, provider);
            navigate('/');
        } catch (err: any) {
            setError('Apple sign-in failed. Please try again.');
        } finally {
            setLoadingProvider(null);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await sendPasswordResetEmail(auth, email);
            setError(null);
            alert('Password reset email sent! Check your inbox.');
            setView('auth');
        } catch (err: any) {
            setError('Could not send reset email. Check the address and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            if (isLogin) {
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                await createUserWithEmailAndPassword(auth, email, password);
            }
            navigate('/');
        } catch (err: any) {
            if (err.code === 'auth/invalid-credential') setError('Invalid email or password. Please try again.');
            else if (err.code === 'auth/email-already-in-use') setError('This email is already registered. Try signing in.');
            else if (err.code === 'auth/weak-password') setError('Password must be at least 6 characters.');
            else setError('Authentication failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden">
            {/* Background grid */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-slate-950 to-slate-950 pointer-events-none" />

            <div className="relative z-10 w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* Logo */}
                <div className="mb-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-primary/10 border border-brand-primary/20 mb-4 relative">
                        <Shield className="w-8 h-8 text-brand-primary" />
                        <span className="absolute text-[11px] font-black text-brand-primary" style={{ marginTop: '1px' }}>E</span>
                    </div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">EMI Flipbook</h1>
                    <p className="text-slate-400 mt-1 text-sm">Your data is 100% Secure & Encrypted</p>
                </div>

                {/* Card */}
                <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-8 shadow-2xl backdrop-blur-sm">
                    {view === 'forgot' ? (
                        <>
                            <h2 className="text-xl font-bold text-white mb-1">Reset Password</h2>
                            <p className="text-slate-400 text-sm mb-6">Enter your email and we'll send a reset link.</p>
                            <form onSubmit={handleForgotPassword} className="space-y-4">
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Email address"
                                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl py-3 pl-10 pr-4 focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none"
                                    />
                                </div>
                                {error && (
                                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                                        <AlertCircle className="w-4 h-4 shrink-0" />
                                        {error}
                                    </div>
                                )}
                                <button type="submit" disabled={loading} className="w-full bg-brand-primary hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2">
                                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                    Send Reset Link
                                </button>
                                <button type="button" onClick={() => { setView('auth'); setError(null); }} className="w-full text-slate-400 text-sm text-center hover:text-white transition-colors pt-1">
                                    ← Back to sign in
                                </button>
                            </form>
                        </>
                    ) : (
                        <>
                            <h2 className="text-xl font-bold text-white mb-1">
                                {isLogin ? 'Welcome back' : 'Create your account'}
                            </h2>
                            <p className="text-slate-400 text-sm mb-6">
                                {isLogin ? 'Sign in to access your private library.' : 'Free forever. No credit card required.'}
                            </p>

                            {error && (
                                <div className="mb-5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                                    <AlertCircle className="w-4 h-4 shrink-0" />
                                    {error}
                                </div>
                            )}

                            {/* Social Buttons */}
                            <div className="space-y-3 mb-6">
                                <button
                                    type="button"
                                    onClick={handleGoogleLogin}
                                    disabled={!!loadingProvider || loading}
                                    className="w-full bg-white hover:bg-slate-100 text-slate-900 font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-3 disabled:opacity-60"
                                >
                                    {loadingProvider === 'google' ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5" alt="Google" />
                                    )}
                                    Continue with Google
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAppleLogin}
                                    disabled={!!loadingProvider || loading}
                                    className="w-full bg-black hover:bg-slate-800 border border-slate-700 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-3 disabled:opacity-60"
                                >
                                    {loadingProvider === 'apple' ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <img src="https://www.svgrepo.com/show/511330/apple-173.svg" className="w-5 h-5 invert" alt="Apple" />
                                    )}
                                    Continue with Apple
                                </button>
                            </div>

                            {/* Divider */}
                            <div className="relative mb-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-slate-700/60" />
                                </div>
                                <div className="relative flex justify-center text-xs">
                                    <span className="px-3 bg-slate-900 text-slate-500 uppercase tracking-wider">Or continue with email</span>
                                </div>
                            </div>

                            {/* Email Form */}
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Email address"
                                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl py-3 pl-10 pr-4 focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none placeholder:text-slate-600"
                                    />
                                </div>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Password"
                                        minLength={6}
                                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-xl py-3 pl-10 pr-12 focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none placeholder:text-slate-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                {isLogin && (
                                    <div className="flex justify-end -mt-2">
                                        <button type="button" onClick={() => { setView('forgot'); setError(null); }} className="text-xs text-brand-primary hover:text-orange-400 transition-colors">
                                            Forgot password?
                                        </button>
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    disabled={loading || !!loadingProvider}
                                    className="w-full bg-brand-primary hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-orange-900/20 flex items-center justify-center gap-2 disabled:opacity-60"
                                >
                                    {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                                    {isLogin ? 'Sign In' : 'Create Account'}
                                </button>
                            </form>

                            <p className="text-slate-400 text-sm text-center mt-5">
                                {isLogin ? "Don't have an account? " : 'Already have an account? '}
                                <button
                                    onClick={() => { setIsLogin(!isLogin); setError(null); }}
                                    className="text-brand-primary hover:text-orange-400 font-medium transition-colors"
                                >
                                    {isLogin ? 'Sign up free' : 'Sign in'}
                                </button>
                            </p>
                        </>
                    )}
                </div>

                {/* Trust Badges */}
                <div className="flex items-center justify-center gap-5 mt-6 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5">🔐 256-bit Encrypted</span>
                    <span className="flex items-center gap-1.5">🛡️ Bank-grade Security</span>
                    <span className="flex items-center gap-1.5">🔒 Private Vault</span>
                </div>
            </div>
        </div>
    );
}
